import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Gift, Copy, Check, Users, Trophy, Share2, Sparkles
} from "lucide-react";
import { toast } from "sonner";

const Referral = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [applyCode, setApplyCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [codeRes, rewardsRes, lbRes] = await Promise.all([
          axios.get(`${API}/referral/code`),
          axios.get(`${API}/referral/rewards`),
          axios.get(`${API}/referral/leaderboard`),
        ]);
        setReferralData(codeRes.data);
        setRewards(rewardsRes.data.rewards || []);
        setLeaderboard(lbRes.data.leaderboard || []);
      } catch (err) {
        if (err.response?.status !== 401) toast.error("Failed to load referral data");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const copyCode = () => {
    if (!referralData?.code) return;
    navigator.clipboard.writeText(referralData.code);
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareCode = () => {
    if (!referralData?.code) return;
    const text = `Join me on LumiFit! Use my referral code ${referralData.code} to get a welcome bonus. ${window.location.origin}/auth`;
    if (navigator.share) {
      navigator.share({ title: "Join LumiFit", text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Share link copied!");
    }
  };

  const handleApply = async () => {
    if (!applyCode.trim()) return;
    try {
      const res = await axios.post(`${API}/referral/apply`, { code: applyCode.trim() });
      toast.success(res.data.message);
      setApplyCode("");
      const rewardsRes = await axios.get(`${API}/referral/rewards`);
      setRewards(rewardsRes.data.rewards || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply code");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50 dark:bg-slate-900" data-testid="referral-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600 hover:text-sky-800 dark:text-sky-400" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t("common.dashboard")}
          </Button>
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
            <span className="text-sm text-sky-700 dark:text-sky-300">Refer a Friend</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Your Referral Code */}
          <div className="glass-card p-6" data-testid="referral-code-section">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                <Gift className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-light text-sky-900 dark:text-sky-100 mb-2" style={{ fontFamily: "Outfit" }}>
                Invite Friends, Earn Rewards
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Share your code and both you and your friend earn bonus rewards
              </p>
            </div>

            <div className="bg-white/60 dark:bg-slate-800/60 rounded-xl p-4 flex items-center justify-between gap-3 mb-4">
              <div className="flex-1">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Your Referral Code</p>
                <p className="text-2xl font-mono font-bold text-sky-700 dark:text-sky-300 tracking-wider" data-testid="referral-code">
                  {referralData?.code || "---"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="outline" onClick={copyCode} className="border-sky-200 dark:border-slate-600" data-testid="copy-code-btn">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-sky-500" />}
                </Button>
                <Button size="icon" onClick={shareCode} className="glow-button" data-testid="share-code-btn">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 text-center">
              <div>
                <p className="text-2xl font-semibold text-sky-700 dark:text-sky-300" style={{ fontFamily: "Outfit" }}>
                  {referralData?.referred_count || 0}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Friends Referred</p>
              </div>
              <div className="w-px h-10 bg-sky-200 dark:bg-slate-600" />
              <div>
                <p className="text-2xl font-semibold text-sky-700 dark:text-sky-300" style={{ fontFamily: "Outfit" }}>
                  {rewards.length}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Rewards Earned</p>
              </div>
            </div>
          </div>

          {/* Apply a Code */}
          <div className="glass-card p-6" data-testid="apply-code-section">
            <h3 className="text-lg font-medium text-sky-800 dark:text-sky-200 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Sparkles className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
              Have a referral code?
            </h3>
            <div className="flex gap-2">
              <Input
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g. LF12AB34)"
                className="flex-1 font-mono"
                data-testid="apply-code-input"
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
              />
              <Button onClick={handleApply} className="glow-button" data-testid="apply-code-btn">
                Apply
              </Button>
            </div>
          </div>

          {/* Rewards */}
          {rewards.length > 0 && (
            <div className="glass-card p-6" data-testid="rewards-section">
              <h3 className="text-lg font-medium text-sky-800 dark:text-sky-200 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <Trophy className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                Your Rewards
              </h3>
              <div className="space-y-2">
                {rewards.map((r) => (
                  <div key={r.id || `${r.type}-${r.rewarded_at}`} className="flex items-center justify-between bg-white/40 dark:bg-slate-800/40 rounded-xl p-3">
                    <div>
                      <p className="text-sm font-medium text-sky-800 dark:text-sky-200">
                        {r.type === "referral_bonus" ? "Referral Bonus" : "Welcome Bonus"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(r.rewarded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${r.claimed ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.claimed ? "Claimed" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="glass-card p-6" data-testid="referral-leaderboard">
              <h3 className="text-lg font-medium text-sky-800 dark:text-sky-200 mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <Users className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
                Top Referrers
              </h3>
              <div className="space-y-2">
                {leaderboard.map((entry, i) => (
                  <div key={entry.user_id} className="flex items-center gap-3 bg-white/40 dark:bg-slate-800/40 rounded-xl p-3">
                    <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
                      i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-100 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-sky-50 text-sky-600"
                    }`}>
                      {i + 1}
                    </span>
                    <p className="flex-1 text-sm font-medium text-sky-800 dark:text-sky-200">{entry.name}</p>
                    <p className="text-sm text-sky-600 dark:text-sky-400">{entry.referrals} referrals</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Referral;
